import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderOtherPriceCalcInput, BimWorkOrderOtherPriceCostData } from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { bimCalcWorkOrderOtherDistributionGql } from '../../bimWorkOrderQueries';

const requestId = 'bimCalcWorkOrderOtherDistribution';

interface IUseBimCalcWorkOrderOtherDistribution {
    workOrderOtherPriceId?: number;
    onCalculation: (items: BimWorkOrderOtherPriceCostData[]) => void;
    onError: () => void;
}

export const useBimCalcWorkOrderOtherDistribution = (props: IUseBimCalcWorkOrderOtherDistribution) => {
    const dispatch = useDispatch();
    const { workOrderOtherPriceId, onCalculation, onError } = props;

    const [
        bimCalcWorkOrderOtherDistributionData,
        bimCalcWorkOrderOtherDistributionStatus,
        ,
        bimCalcWorkOrderOtherDistributionMakeRequest,
    ] = useRequest({
        requestId,
        query: bimCalcWorkOrderOtherDistributionGql,
        withEffect: false,
    });

    const [bimCalcWorkOrderOtherDistributionReset] = useResetStoreData({
        requestId,
    });

    useEffect(() => {
        bimCalcWorkOrderOtherDistributionReset(undefined);
    }, [bimCalcWorkOrderOtherDistributionReset]);

    useEffect(() => {
        if (
            bimCalcWorkOrderOtherDistributionStatus === RequestStatus.SUCCESS &&
            bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.result
        ) {
            onCalculation(
                (bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.data ||
                    []) as BimWorkOrderOtherPriceCostData[],
            );

            bimCalcWorkOrderOtherDistributionReset(undefined);
        }
    }, [
        bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.data,
        bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.result,
        bimCalcWorkOrderOtherDistributionReset,
        bimCalcWorkOrderOtherDistributionStatus,
        onCalculation,
    ]);

    useEffect(() => {
        if (
            bimCalcWorkOrderOtherDistributionStatus === RequestStatus.ERROR ||
            (bimCalcWorkOrderOtherDistributionStatus === RequestStatus.SUCCESS &&
                !bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.result)
        ) {
            onError();
            bimCalcWorkOrderOtherDistributionReset(undefined);
        }
    }, [
        bimCalcWorkOrderOtherDistributionData?.bimCalcWorkOrderOtherDistribution?.result,
        bimCalcWorkOrderOtherDistributionReset,
        bimCalcWorkOrderOtherDistributionStatus,
        onError,
    ]);

    const bimCalcWorkOrderOtherDistributionRequest = useCallback(
        (input: BimWorkOrderOtherPriceCalcInput) => {
            dispatch(bimCalcWorkOrderOtherDistributionMakeRequest({ workOrderOtherPriceId, input }));
        },
        [bimCalcWorkOrderOtherDistributionMakeRequest, dispatch, workOrderOtherPriceId],
    );

    const debouncedChange = useDebounce(bimCalcWorkOrderOtherDistributionRequest, 700);

    return {
        bimCalcWorkOrderOtherDistributionStatus,
        bimCalcWorkOrderOtherDistributionRequest: debouncedChange,
    };
};
